import React, { useState, useEffect, useRef } from 'react';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import BarChartWithTable from '../components/charts/BarChartWithTable';
import { apiCall } from '@/api/apiCall';
import { useQueue } from '@/context/QueueContext';
import { processParams } from '../utils/paramsHelper';
import { useNavigate } from 'react-router-dom';

const KpiChartNew = ({ year = "", month = "", monthNumber = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const hasFetchedRef             = useRef(false);

  /**
   * Field Detection Rules:
   *  - "Prior Year" key → priorYear bar  (always)
   *  - "Budget"     key → budget bar     (always)
   *  - "Actual"     key → actual bar     (always)
   *  - "Base Rent"  key → baseRent line  GREEN  — round dotted (value_type 2, 3)
   *  - "Base"       key → base line      RED    — solid        (value_type 4)
   *  - "Occupancy"  key → occupancy line YELLOW — solid        (value_type 4)
   *
   * Line style guide:
   *  baseLines item with NO dashPattern (or dashPattern: []) → solid line
   *  baseLines item with dashPattern + isRound               → round dotted line
   */
  const transformApiData = (apiResponse) => {
    const data = apiResponse?.data;
    if (!data || typeof data !== 'object') return null;

    const value_type = page?.params?.value_type;

    const parseValue = (value) => {
      if (value === undefined || value === null) return 0;
      if (typeof value === 'number') return value;
      return parseFloat(String(value).replace(/,/g, '').replace('%', '')) || 0;
    };

    const topKeys       = Object.keys(data);
    const isYearGrouped = topKeys.length > 0 && topKeys.every(k => /^\d{4}$/.test(k));

    if (isYearGrouped) {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const labels = [], priorYear = [], budget = [], actual = [];
      const baseRent = [], base = [], occupancy = [], occupancyRaw = [];
      const yearRanges = [];

      let detectedFields = null;
      for (const yr of topKeys) {
        const yearData = data[yr];
        if (!yearData) continue;
        const firstMonthKey = monthOrder.find(m => yearData[m] != null);
        if (firstMonthKey) {
          detectedFields = Object.keys(yearData[firstMonthKey]);
          break;
        }
      }

      const hasBaseRent  = detectedFields && detectedFields.includes('Base Rent');
      const hasBase      = detectedFields && detectedFields.includes('Base');
      const hasOccupancy = detectedFields && detectedFields.includes('Occupancy');

      topKeys.forEach(yr => {
        const yearData = data[yr];
        if (!yearData || typeof yearData !== 'object') return;
        const monthsInYear = monthOrder.filter(m => yearData[m] != null);
        if (monthsInYear.length === 0) return;

        yearRanges.push({ year: yr, span: monthsInYear.length });
        monthsInYear.forEach(m => {
          labels.push(m);
          priorYear.push(parseValue(yearData[m]['Prior Year']));
          budget.push(parseValue(yearData[m]['Budget']));
          actual.push(parseValue(yearData[m]['Actual']));
          if (hasBaseRent)  baseRent.push(parseValue(yearData[m]['Base Rent']));
          if (hasBase)      base.push(parseValue(yearData[m]['Base']));
          if (hasOccupancy) {
            occupancy.push(parseValue(yearData[m]['Occupancy']));
            occupancyRaw.push(yearData[m]['Occupancy'] ?? '');
          }
        });
      });

      const availableFields = ['priorYear', 'budget', 'actual'];
      const transformed = {
        labels, yearRanges, priorYear, budget, actual,
        isPercent: false, availableFields,
      };
      if (hasBaseRent)  transformed.baseRent     = baseRent;
      if (hasBase)      transformed.base         = base;
      if (hasOccupancy) {
        transformed.occupancy    = occupancy;
        transformed.occupancyRaw = occupancyRaw;
      }
      return transformed;
    }

    // ─── Flat format ──────────────────────────────────────────────────────────
    const monthKeys = Object.keys(data).filter(key => {
      const val = data[key];
      return val && typeof val === 'object' && !Array.isArray(val);
    });
    if (monthKeys.length === 0) return null;

    const firstMonthKeys = Object.keys(data[monthKeys[0]]);
    const hasBaseRent    = firstMonthKeys.includes('Base Rent');
    const hasBase        = firstMonthKeys.includes('Base');
    const hasOccupancy   = firstMonthKeys.includes('Occupancy');

    const availableFields = ['priorYear', 'budget', 'actual'];
    const transformed = {
      labels:         monthKeys,
      priorYear:      monthKeys.map(m => parseValue(data[m]['Prior Year'])),
      budget:         monthKeys.map(m => parseValue(data[m]['Budget'])),
      actual:         monthKeys.map(m => parseValue(data[m]['Actual'])),
      isPercent:      false,
      availableFields,
    };
    if (hasBaseRent) transformed.baseRent     = monthKeys.map(m => parseValue(data[m]['Base Rent']));
    if (hasBase)     transformed.base         = monthKeys.map(m => parseValue(data[m]['Base']));
    if (hasOccupancy) {
      transformed.occupancy    = monthKeys.map(m => parseValue(data[m]['Occupancy']));
      transformed.occupancyRaw = monthKeys.map(m => data[m]['Occupancy'] ?? '');
    }
    return transformed;
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiParams = processParams(page?.params || {}, { year, month, contract });
      const response  = await enqueue(() => apiCall("GET", page?.api, apiParams,navigate,false,false,isRefresh));
      if (response?.success) {
        const transformed = transformApiData(response);
        if (transformed) {
          setChartData(transformed);
        } else {
          setError("No data available");
        }
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching KPI chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading)    return <LoadingComponent />;
  if (error)      return <ErrorState error={error} onRetry={fetchData} />;
  if (!chartData) return <ErrorState error="No data available" onRetry={fetchData} />;

  /**
   * baseLines line style:
   *  - dashPattern: [0.1, 9], isRound: true  → round dotted  (Base Rent)
   *  - solid: true                            → solid line    (Base, Occupancy)
   */
  const baseLines = [
    ...(chartData.baseRent
      ? [{
          key:         'baseRent',
          label:       'Base Rent',
          color:       '#A9D18E',
          dashPattern: [0.1, 9],
          isRound:     true,
          solid:       false,
        }]
      : []),
    ...(chartData.base
      ? [{
          key:   'base',
          label: 'Base',
          color: '#C90A0A',
          solid: true,
        }]
      : []),
    ...(chartData.occupancy
      ? [{
          key:   'occupancy',
          label: 'Occupancy',
          color: '#FFD700',
          solid: true,
        }]
      : []),
  ];

  const replacePlaceholders = (str = '') =>
    str
      .replace(/{{year}}/g,     year)
      .replace(/{{month}}/g,    month)
      .replace(/{{contract}}/g, contract);

  return (
    <BarChartWithTable
      title={page?.title || 'KPI Chart'}
      subtitle={replacePlaceholders(page?.subtitle)}
      inChartContent={replacePlaceholders(page?.in_chart_content_line_1)}
      data={chartData}
      chartHeight={page?.chartHeight || "400px"}
      showLegend={page?.showLegend !== false}
      showTable={page?.showTable !== false}
      baseLines={baseLines}
    />
  );
};

export default KpiChartNew;